export type PatientStatus = 'ACTIVE' | 'SCHEDULED' | 'WAITING' | 'CANCELLED' | 'INACTIVE' | 'DECEASED' | 'ARCHIVED';
export type Sex = 'M' | 'F' | 'O';

export type AllergyEntry = { name: string; reaction?: string };
export type ProblemEntry = { name: string; since?: string };
export type MedicationEntry = { name: string; dose?: string };

export type PatientRow = {
  id: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  avatarUrl?: string | null;
  avatarData?: string | null;
  mrn: string;
  age: number;
  sex: Sex;
  dob: string;
  phone: string;
  phoneSecondary?: string;
  email: string;
  status: PatientStatus;
  lastVisit: string;
  provider: string;
  flags: Array<'allergy' | 'high-risk' | 'vip'>;
  allergies: string[];
  problems: string[];
  medications: string[];
  upcoming?: string;
  primaryProviderId?: string | null;
  preferredPharmacyId?: string | null;
  preferredPharmacyName?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  country?: string | null;
  formattedAddress?: string | null;
  googlePlaceId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  source?: string | null;
  address?: string;
  insuranceProvider?: string | null;
  insuranceMemberId?: string | null;
  insuranceGroupNumber?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  genderIdentity?: string | null;
  allergiesRaw: AllergyEntry[];
  problemsRaw: ProblemEntry[];
  medicationsRaw: MedicationEntry[];
};

export type PatientFormState = {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  sex: string;
  genderIdentity: string;
  mrn: string;
  phone: string;
  email: string;
  address: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  formattedAddress: string;
  googlePlaceId: string;
  latitude: string;
  longitude: string;
  source: string;
  insuranceProvider: string;
  insuranceMemberId: string;
  insuranceGroupNumber: string;
  emergencyName: string;
  emergencyPhone: string;
  clinicalNotes: string;
  primaryProviderId: string;
  preferredPharmacyId: string;
  avatarUrl: string;
  avatarData: string;
  status: PatientStatus;
  allergies: string;
  flags: PatientRow['flags'];
};

export const statusColors: Record<PatientStatus, string> = {
  ACTIVE: 'pill-success',
  INACTIVE: 'pill-warn',
  DECEASED: 'pill-danger',
  ARCHIVED: 'pill-warn',
  WAITING: 'pill-info',
  SCHEDULED: 'pill-info',
  CANCELLED: 'pill-danger',
};

export const statusLabels: Record<PatientStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DECEASED: 'Deceased',
  ARCHIVED: 'Archived',
  WAITING: 'Waiting',
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
};

export const statuses: PatientStatus[] = ['ACTIVE', 'INACTIVE', 'DECEASED', 'ARCHIVED', 'WAITING', 'SCHEDULED', 'CANCELLED'];

export function parseAllergies(allergies: unknown): AllergyEntry[] {
  if (!Array.isArray(allergies)) return [];
  return allergies
    .map((item) => {
      if (typeof item === 'string') return { name: item } as AllergyEntry;
      if (item && typeof item === 'object') {
        const name = (item as any).name ?? '';
        const reaction = (item as any).reaction ?? (typeof (item as any).severity === 'string' ? (item as any).severity : '');
        if (!name && !reaction) return null;
        return { name, reaction: reaction || undefined };
      }
      return null;
    })
    .filter(Boolean) as AllergyEntry[];
}

export function parseProblems(problems: unknown): ProblemEntry[] {
  if (!Array.isArray(problems)) return [];
  return problems
    .map((item) => {
      if (typeof item === 'string') return { name: item } as ProblemEntry;
      if (item && typeof item === 'object') {
        const name = (item as any).name ?? '';
        const since = (item as any).since ?? (item as any).onset;
        if (!name && !since) return null;
        return { name, since: since || undefined };
      }
      return null;
    })
    .filter(Boolean) as ProblemEntry[];
}

export function parseMedications(medications: unknown): MedicationEntry[] {
  if (!Array.isArray(medications)) return [];
  return medications
    .map((item) => {
      if (typeof item === 'string') return { name: item } as MedicationEntry;
      if (item && typeof item === 'object') {
        const name = (item as any).name ?? '';
        const dose = (item as any).dose ?? (item as any).direction;
        if (!name && !dose) return null;
        return { name, dose: dose || undefined };
      }
      return null;
    })
    .filter(Boolean) as MedicationEntry[];
}

export function formatAllergies(entries: AllergyEntry[]): string[] {
  return entries.map((a) => [a.name, a.reaction].filter(Boolean).join(' - ')).filter(Boolean);
}

export function formatProblems(entries: ProblemEntry[]): string[] {
  return entries.map((p) => [p.name, p.since ? `since ${p.since}` : null].filter(Boolean).join(' - ')).filter(Boolean);
}

export function formatMedications(entries: MedicationEntry[]): string[] {
  return entries.map((m) => [m.name, m.dose].filter(Boolean).join(' - ')).filter(Boolean);
}

export function emptyFormState(): PatientFormState {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    dob: '',
    sex: 'F',
    genderIdentity: '',
    mrn: '',
    phone: '',
    email: '',
    address: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    formattedAddress: '',
    googlePlaceId: '',
    latitude: '',
    longitude: '',
    source: 'MANUAL',
    insuranceProvider: '',
    insuranceMemberId: '',
    insuranceGroupNumber: '',
    emergencyName: '',
    emergencyPhone: '',
    clinicalNotes: '',
    primaryProviderId: '',
    preferredPharmacyId: '',
    avatarUrl: '',
    avatarData: '',
    status: 'ACTIVE',
    allergies: '',
    flags: [],
  };
}

export function mapPatientFromApi(p: any): PatientRow {
  const providerName =
    p?.primaryProvider?.name ??
    [p?.primaryProvider?.firstName, p?.primaryProvider?.lastName].filter(Boolean).join(' ').trim();

  const allergiesRaw = parseAllergies(p.allergies);
  const problemsRaw = parseProblems(p.problems);
  const medicationsRaw = parseMedications(p.medications);

  return {
    id: p.id,
    firstName: p.firstName ?? '',
    middleName: p.middleName ?? null,
    lastName: p.lastName ?? '',
    avatarUrl: p.avatarUrl ?? p.photoUrl ?? p.avatar ?? p.avatarData ?? null,
    mrn: p.mrn ?? '',
    age: p.dob ? new Date().getFullYear() - new Date(p.dob).getFullYear() : 0,
    sex: (p.sex as Sex) || 'O',
    dob: p.dob || '',
    phone: p.phone || '',
    phoneSecondary: p.phoneSecondary ?? '',
    email: p.email || '',
    status: (p.status as PatientStatus) || 'ACTIVE',
    lastVisit: p.lastVisitAt || '',
    provider: providerName || '',
    primaryProviderId: p.primaryProvider?.id ?? null,
    preferredPharmacyId: p.preferredPharmacy?.id ?? null,
    preferredPharmacyName: p.preferredPharmacy?.name ?? null,
    flags: p.flags || [],
    allergies: formatAllergies(allergiesRaw),
    problems: formatProblems(problemsRaw),
    medications: formatMedications(medicationsRaw),
    allergiesRaw,
    problemsRaw,
    medicationsRaw,
    upcoming: '',
    address: [p.address?.line1 ?? p.addressLine1, p.address?.line2 ?? p.addressLine2, p.city, p.state, p.postalCode, p.address?.country ?? p.country]
      .filter(Boolean)
      .join(', '),
    addressLine1: p.address?.line1 ?? p.addressLine1 ?? null,
    addressLine2: p.address?.line2 ?? p.addressLine2 ?? null,
    city: p.city ?? null,
    state: p.state ?? null,
    postalCode: p.postalCode ?? null,
    country: p.address?.country ?? p.country ?? null,
    formattedAddress: p.address?.formattedAddress ?? p.formattedAddress ?? null,
    googlePlaceId: p.address?.googlePlaceId ?? p.googlePlaceId ?? null,
    latitude: p.address?.latitude ?? p.latitude ?? null,
    longitude: p.address?.longitude ?? p.longitude ?? null,
    source: p.address?.source ?? p.source ?? null,
    insuranceProvider: p.insuranceProvider ?? null,
    insuranceMemberId: p.insuranceMemberId ?? null,
    insuranceGroupNumber: p.insuranceGroupNumber ?? null,
    emergencyContactName: p.emergencyContactName ?? null,
    emergencyContactPhone: p.emergencyContactPhone ?? null,
    genderIdentity: p.genderIdentity ?? null,
  };
}
